import React, { useState } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import clsx from 'clsx';
import {
    FileText,
    Sparkles,
    Lock,
    Search,
    Crown,
    BarChart3,
    ClipboardCheck,
    TrendingUp,
} from 'lucide-react';
import {
    REPORT_TEMPLATES,
    exportPfmeaWorksheetPDF,
    exportPfmeaWorksheetExcel,
    exportRiskMatrixPDF,
    exportExecutiveSummaryPDF,
} from '../services/reportGenerator';

const ReportsPage: React.FC = () => {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const {
        pfmeaItems,
        activeProject,
        standardConfig,
        workflowSteps,
    } = useAppContext();

    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [generating, setGenerating] = useState<string | null>(null);

    // Check if user has Prime Mode
    const isPrimeMode = (user as any)?.customClaims?.subscription === 'prime' || false;

    // Calculate workflow progress
    const workflowProgress = Math.round(
        (workflowSteps.filter(s => s.status === 'completed').length / workflowSteps.length) * 100
    );

    // Filter reports
    const filteredReports = REPORT_TEMPLATES.filter(report => {
        const matchesCategory = selectedCategory === 'all' || report.category === selectedCategory;
        const matchesSearch = report.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            report.description.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const categories = [
        { id: 'all', label: 'All Reports', icon: <FileText size={16} />, count: REPORT_TEMPLATES.length },
        { id: 'pfmea', label: 'PFMEA Analysis', icon: <BarChart3 size={16} />, count: REPORT_TEMPLATES.filter(r => r.category === 'pfmea').length },
        { id: 'control', label: 'Control & Compliance', icon: <ClipboardCheck size={16} />, count: REPORT_TEMPLATES.filter(r => r.category === 'control').length },
        { id: 'management', label: 'Management', icon: <TrendingUp size={16} />, count: REPORT_TEMPLATES.filter(r => r.category === 'management').length },
        { id: 'ai', label: 'AI-Powered', icon: <Sparkles size={16} />, count: REPORT_TEMPLATES.filter(r => r.category === 'ai').length },
    ];

    const handleGenerateReport = async (report: typeof REPORT_TEMPLATES[0], format: 'pdf' | 'excel' | 'csv') => {
        if (report.requiresPrime && !isPrimeMode) {
            // Show upgrade modal
            alert('This report requires Prime Mode subscription. Please upgrade to access AI-powered reports.');
            return;
        }

        setGenerating(report.id);

        try {
            switch (report.id) {
                case 'pfmea-worksheet':
                    if (format === 'pdf') {
                        await exportPfmeaWorksheetPDF(pfmeaItems, activeProject || 'PFMEA Project', standardConfig?.name || 'AIAG-VDA');
                    } else if (format === 'excel') {
                        await exportPfmeaWorksheetExcel(pfmeaItems, activeProject || 'PFMEA Project', standardConfig?.name || 'AIAG-VDA');
                    }
                    break;
                case 'risk-matrix':
                    await exportRiskMatrixPDF(pfmeaItems, activeProject || 'PFMEA Project');
                    break;
                case 'executive-summary':
                    await exportExecutiveSummaryPDF(pfmeaItems, activeProject || 'PFMEA Project', workflowProgress);
                    break;
                // Add more cases as reports are implemented
                default:
                    alert(`Report "${report.name}" generation coming soon!`);
            }
        } catch (error) {
            console.error('Error generating report:', error);
            alert('Failed to generate report. Please try again.');
        } finally {
            setGenerating(null);
        }
    };

    const getFormatIcon = (format: string) => {
        switch (format) {
            case 'pdf': return '📄';
            case 'excel': return '📊';
            case 'csv': return '📋';
            default: return '📁';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--text-primary)] flex items-center gap-2">
                        <FileText className="text-primary-500" />
                        Reports Center
                    </h1>
                    <p className="text-[var(--text-secondary)]">
                        Generate and export PFMEA reports in multiple formats
                    </p>
                </div>

                {/* Prime Mode Badge */}
                {isPrimeMode ? (
                    <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full">
                        <Crown size={18} />
                        <span className="font-medium">Prime Mode Active</span>
                    </div>
                ) : (
                    <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity">
                        <Sparkles size={18} />
                        Upgrade to Prime
                    </button>
                )}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className={clsx(
                    'p-4 rounded-xl border',
                    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                )}>
                    <p className="text-sm text-[var(--text-secondary)]">PFMEA Items</p>
                    <p className="text-2xl font-bold text-[var(--text-primary)]">{pfmeaItems.length}</p>
                </div>
                <div className={clsx(
                    'p-4 rounded-xl border',
                    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                )}>
                    <p className="text-sm text-[var(--text-secondary)]">High Priority</p>
                    <p className="text-2xl font-bold text-red-500">
                        {pfmeaItems.filter(i => i.actionPriority === 'H').length}
                    </p>
                </div>
                <div className={clsx(
                    'p-4 rounded-xl border',
                    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                )}>
                    <p className="text-sm text-[var(--text-secondary)]">Workflow Progress</p>
                    <p className="text-2xl font-bold text-green-500">{workflowProgress}%</p>
                </div>
                <div className={clsx(
                    'p-4 rounded-xl border',
                    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                )}>
                    <p className="text-sm text-[var(--text-secondary)]">Available Reports</p>
                    <p className="text-2xl font-bold text-primary-500">{REPORT_TEMPLATES.length}</p>
                </div>
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
                    <input
                        type="text"
                        placeholder="Search reports..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={clsx(
                            'w-full pl-10 pr-4 py-2.5 rounded-xl border',
                            isDarkMode
                                ? 'bg-slate-800 border-slate-700 text-white'
                                : 'bg-white border-slate-200 text-slate-800'
                        )}
                    />
                </div>

                {/* Category Filter Tabs */}
                <div className={clsx(
                    'flex items-center gap-1 p-1 rounded-xl',
                    isDarkMode ? 'bg-slate-800' : 'bg-slate-100'
                )}>
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setSelectedCategory(cat.id)}
                            className={clsx(
                                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                                selectedCategory === cat.id
                                    ? 'bg-primary-500 text-white'
                                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                            )}
                        >
                            {cat.icon}
                            <span className="hidden md:inline">{cat.label}</span>
                            <span className={clsx(
                                'px-1.5 py-0.5 rounded-full text-xs',
                                selectedCategory === cat.id
                                    ? 'bg-white/20'
                                    : isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                            )}>
                                {cat.count}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Report Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredReports.map(report => {
                    const isLocked = report.requiresPrime && !isPrimeMode;
                    const isGenerating = generating === report.id;

                    return (
                        <div
                            key={report.id}
                            className={clsx(
                                'relative p-5 rounded-xl border transition-all',
                                isDarkMode
                                    ? 'bg-slate-800 border-slate-700 hover:border-slate-600'
                                    : 'bg-white border-slate-200 hover:border-slate-300',
                                isLocked && 'opacity-75'
                            )}
                        >
                            {/* Prime Badge */}
                            {report.requiresPrime && (
                                <div className="absolute top-3 right-3">
                                    {isPrimeMode ? (
                                        <span className="px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium rounded-full">
                                            Prime
                                        </span>
                                    ) : (
                                        <Lock size={18} className="text-slate-400" />
                                    )}
                                </div>
                            )}

                            {/* Icon and Title */}
                            <div className="flex items-start gap-3 mb-3">
                                <div className={clsx(
                                    'p-3 rounded-xl text-2xl',
                                    report.category === 'pfmea' && 'bg-blue-100 dark:bg-blue-900/30',
                                    report.category === 'control' && 'bg-green-100 dark:bg-green-900/30',
                                    report.category === 'management' && 'bg-purple-100 dark:bg-purple-900/30',
                                    report.category === 'ai' && 'bg-amber-100 dark:bg-amber-900/30',
                                )}>
                                    {report.icon}
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-[var(--text-primary)]">
                                        {report.name}
                                    </h3>
                                    <p className="text-sm text-[var(--text-secondary)]">
                                        {report.description}
                                    </p>
                                </div>
                            </div>

                            {/* Export Buttons */}
                            <div className="flex items-center gap-2 mt-4">
                                {report.formats.map(format => (
                                    <button
                                        key={format}
                                        onClick={() => handleGenerateReport(report, format)}
                                        disabled={isLocked || isGenerating}
                                        className={clsx(
                                            'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                                            isLocked
                                                ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                                                : format === 'pdf'
                                                    ? 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                                                    : format === 'excel'
                                                        ? 'bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                                                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400'
                                        )}
                                    >
                                        {isGenerating ? (
                                            <span className="animate-spin">⏳</span>
                                        ) : (
                                            <>
                                                {getFormatIcon(format)}
                                                {format.toUpperCase()}
                                            </>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Empty State */}
            {filteredReports.length === 0 && (
                <div className="text-center py-12">
                    <FileText className="mx-auto text-slate-300 mb-4" size={48} />
                    <p className="text-lg font-medium text-[var(--text-primary)]">No reports found</p>
                    <p className="text-[var(--text-secondary)]">Try adjusting your search or filter</p>
                </div>
            )}

            {/* AI Reports Promo */}
            {!isPrimeMode && (
                <div className={clsx(
                    'p-6 rounded-xl border-2 border-dashed',
                    isDarkMode ? 'border-purple-800 bg-purple-900/20' : 'border-purple-300 bg-purple-50'
                )}>
                    <div className="flex items-center gap-4">
                        <div className="p-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl text-white">
                            <Sparkles size={32} />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-[var(--text-primary)]">
                                Unlock AI-Powered Reports
                            </h3>
                            <p className="text-[var(--text-secondary)]">
                                Upgrade to Prime Mode for AI Risk Assessment, Predictive Analysis, and Comprehensive Project Reports
                            </p>
                        </div>
                        <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity">
                            Upgrade Now
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportsPage;
